import React, { Component } from "react";
import { connect } from "react-redux";
import { Document, Page, pdfjs } from "react-pdf";
import { Dialog, DialogFooter, DialogType } from "office-ui-fabric-react/lib-commonjs/Dialog";
import { DefaultButton, PrimaryButton } from "office-ui-fabric-react/lib-commonjs/Button";
import { SpinButton } from "office-ui-fabric-react/lib-commonjs/SpinButton";
import { changeNumOfPages, changePage, closeDialog, selectPage } from "../../actions/pdfdialog-actions";
import "./index.css";
import { setPdfImperialUnit, setPdfPageSizes } from "../../actions/pdf-actions";
import { METRIC } from "../../lib/types";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

class PDFDialog extends Component{

    result = "";
    pageSize = {
        ptWidth: 0,
        ptHeight: 0,
        inWidth: 0,
        inHeight: 0,
        mmWidth: 0,
        mmHeight: 0,
    };

    /**
     * Obtains the page size for different purposes.
     * @param page
     * @returns {{mmHeight: number, mmWidth: number, ptWidth: number, ptHeight: number, inHeight: number, inWidth: number}}
     */
    getPageSize( page ) {
        try {
            let x, y, w, h, rotate;
            // Hacking a fix for the bundled version od pdf.js, apparently the access to the
            // property varies on the server
            if (page.pageInfo !== undefined) {
                [x, y, w, h] = page.pageInfo.view;
                rotate = page.pageInfo.rotate;
            } else {
                [x, y, w, h] = page._pageInfo.view;
                rotate = page._pageInfo.rotate;
            }
            const width = w - x;
            const height = h - y;

            return ( rotate === 90 || rotate === 270 )
                ? this.pageSize = {
                    ptWidth: height,
                    ptHeight: width,
                    inWidth: parseFloat(( height / 72 ).toFixed(2)),
                    inHeight: parseFloat(( width / 72 ).toFixed(2)),
                    mmWidth: parseFloat(( height / 2.835 ).toFixed(2)),
                    mmHeight: parseFloat(( width / 2.835 ).toFixed(2)),
                }
                : this.pageSize = {
                    ptWidth: width,
                    ptHeight: height,
                    inWidth: parseFloat(( width / 72 ).toFixed(2)),
                    inHeight: parseFloat(( height / 72 ).toFixed(2)),
                    mmWidth: parseFloat(( width / 2.835 ).toFixed(2)),
                    mmHeight: parseFloat(( height / 2.835 ).toFixed(2)),
                };
        } catch (err) {
            console.error("PDFDialog \"getPageSize()\" - " + err.message);
            return this.pageSize = {
                ptWidth: 0,
                ptHeight: 0,
                inWidth: 0,
                inHeight: 0,
                mmWidth: 0,
                mmHeight: 0,
            };
        }
    }

    /**
     * Handles various PDF loading stpes when dialog is closed.
     * @param proceed
     */
    handleDialogClose( proceed ) {
        /* this object reference */
        let self = this;
        /* if page accepted */
        if (proceed) {
            /* Read the file using file reader */
            let fileReader = new FileReader();
            /* when parsing has finish */
            fileReader.onload = function() {
                /* turn array buffer into typed array */
                let typeArray = new Uint8Array(this.result);
                /* PDFJS will parse the typed array */
                pdfjs.getDocument(typeArray)
                    .promise
                    .then(function( pdf ) {
                        /* Fetch the desired page */
                        pdf.getPage(self.props.pageNumber)
                            .then(page => {
                                // Obtain PDF sizes and save them

                                const pdfPageSize = self.getPageSize(page);
                                self.props.setPdfPageSizes(pdfPageSize);

                                if (self.props.unit === METRIC) {
                                    self.props.setPdfImperialUnit(false);
                                } else {
                                    self.props.setPdfImperialUnit(true);
                                }

                                /* set parameters */
                                let scale = 1;
                                let viewport = page.getViewport(
                                    { scale },
                                );

                                /* Prepare canvas using PDF page dimensions */
                                let canvas = document.createElement("canvas");
                                let context = canvas.getContext("2d");
                                canvas.height = viewport.height;
                                canvas.width = viewport.width;

                                /* Render PDF page into canvas context */
                                let task = page.render({
                                    canvasContext: context,
                                    viewport: viewport,
                                });

                                /* wait for the render to finish */
                                task.promise
                                    .then(() => {
                                        /* the image */
                                        let img = new window.Image();
                                        /* on image load, set the image to konva */
                                        img.onload = () => {
                                            /* calling background loaded action */
                                            self.props.selectPage(img);
                                        };
                                        /* set the source */
                                        img.src = canvas.toDataURL("image/jpeg");
                                    });
                            })
                            .catch(function( err ) {
                                console.error("PDFDialog \"handleDialogClose()\" on pdf.getPage - " + err.message);
                            });
                    })
                    .catch(function( err ) {
                        console.error("PDFDialog \"handleDialogClose()\" on pdfjs.getDocument - " + err.message);
                    });
            };
            /*Read the file as ArrayBuffer */
            fileReader.readAsArrayBuffer(this.props.file);
        } else {
            this.props.closeDialog();
        }
    }

    handleOnDocumentLoad( pdf ) {
        /* if it is only one page, render that */
        this.props.changeNumOfPages(pdf.numPages);
    }

    handlePageChange( change ) {
        /* get the page number from state */
        let pageNumber = this.props.pageNumber;
        /* if change is in bounds, compute and return */
        if (
            pageNumber + change >= 1 &&
            pageNumber + change <= this.props.numPages
        ) {
            pageNumber += change;
            this.props.changePage(pageNumber);
        }
        return String(pageNumber);
    }

    handleOnValidate( value ) {
        if (isNaN(value)) {
            return;
        }
        /* get the integer value */
        value = parseInt(value, 10);
        /* if change is in bounds, compute and return */
        if (value >= 1 && value <= this.props.numPages) {
            this.props.changePage(value);
        }
        return String(value);
    }

    render() {
        const {
            lang,
            file,
            hidden,
            content,
            numPages,
            pageNumber,
        } = this.props;

        return (
            <Dialog
                className={"pdfDialog"}
                hidden={hidden}
                onDismiss={() => this.handleDialogClose(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: content[lang].pdfDocumentPage,
                    subText: content[lang].selectPdfDesiredPage,
                }}
                modalProps={{
                    isBlocking: true,
                    className: "pdfDialog",
                }}
            >
                <Document
                    file={file}
                    onLoadSuccess={this.handleOnDocumentLoad.bind(this)}
                    loading={content[lang].loadingPdf}
                    error={content[lang].failedToLoadPdf}
                >
                    <Page
                        loading={content[lang].loadingPdfPage}
                        className={"pdfPage"}
                        pageNumber={pageNumber}
                        scale={0.4}
                    />
                </Document>
                <div style={{ display: "flex" }} id="DivChromeBack">
                    <DefaultButton
                        primary={true}
                        iconProps={{ iconName: "ChromeBack" }}
                        style={{ minWidth: "30px", maxWidth: "30px" }}
                        onClick={this.handlePageChange.bind(this, -1)}
                    />
                    <SpinButton
                        min={1}
                        max={numPages}
                        step={1}
                        value={pageNumber}
                        // onIncrement={this.handlePageChange.bind(this, 1)}
                        // onDecrement={this.handlePageChange.bind(this, -1)}
                        onValidate={this.handleOnValidate.bind(this)}
                    />
                    <DefaultButton
                        primary={true}
                        style={{ minWidth: "30px", maxWidth: "30px" }}
                        iconProps={{ iconName: "ChromeBackMirrored" }}
                        onClick={this.handlePageChange.bind(this, 1)}
                    />
                </div>
                <DialogFooter>
                    {
                        numPages !== 1
                            ?
                            <PrimaryButton
                                onClick={() => this.handleDialogClose(true)}
                                text={content[lang].select}
                            />
                            : this.handleDialogClose(true)
                    }
                    <DefaultButton
                        onClick={() => this.handleDialogClose(false)}
                        text={content[lang].cancel}
                        className={"outline_grey_ads"}
                    />
                </DialogFooter>
            </Dialog>
        );
    }
}

const mapStateToProps = state => {
    return {
        file: state.pdfdialog.file,
        lang: state.localization.lang,
        unit: state.localization.unit,
        hidden: state.pdfdialog.hidden,
        numPages: state.pdfdialog.numPages,
        content: state.localization.content,
        pageNumber: state.pdfdialog.pageNumber,
    };
};

const mapDispatchToProps = {
    selectPage,
    changePage,
    closeDialog,
    changeNumOfPages,
    setPdfPageSizes,
    setPdfImperialUnit,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(PDFDialog);



// WEBPACK FOOTER //
// ./src/components/PDFDialog/index.js